// Story 13.1 — Wizard web de creación de bot. Modal multi-step:
//   1. Template (cards selector)
//   2. Identity (nombre + business + voice)
//   3. Provider (cloud-api / evolution-go)
//   4. Review (YAML preview + crear)
//
// Reutiliza el componente `Modal` existente (sin shadcn Dialog completo)
// y el patrón de mutation con TanStack Query del proyecto.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useReducer, useState } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/editor/dialogs/Modal';
import { Button } from '@/components/ui/button';
import {
  MENU_MODE_LABELS,
  type MenuMode,
  MenuModeSelect,
} from '@/components/whatsapp-connection/MenuModeSelect';
import { useMe } from '@/hooks/queries/use-me';
import {
  type BotRowSummary,
  botsApi,
  type CreateBotFromTemplateBody,
  type TemplateMetadataDto,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';

// Story 13.x cleanup — preset compartido CLI↔web. Lista hardcoded para evitar
// el roundtrip de cargar `@revelation-bots/core` en el bundle del browser
// (mantiene el chunk lean). Si la lista del CLI cambia, esta debe sincronizar
// — los tests del wizard fallan si divergen.
const TIMEZONE_PRESETS: ReadonlyArray<{ value: string; label: string; offset: string }> = [
  { value: 'America/Mexico_City', label: 'Ciudad de México', offset: 'UTC-6' },
  { value: 'America/Cancun', label: 'Cancún', offset: 'UTC-5' },
  { value: 'America/Tijuana', label: 'Tijuana', offset: 'UTC-8' },
  { value: 'America/Guatemala', label: 'Guatemala', offset: 'UTC-6' },
  { value: 'America/El_Salvador', label: 'San Salvador', offset: 'UTC-6' },
  { value: 'America/Tegucigalpa', label: 'Tegucigalpa', offset: 'UTC-6' },
  { value: 'America/Managua', label: 'Managua', offset: 'UTC-6' },
  { value: 'America/Costa_Rica', label: 'San José (CR)', offset: 'UTC-6' },
  { value: 'America/Panama', label: 'Panamá', offset: 'UTC-5' },
  { value: 'America/Bogota', label: 'Bogotá', offset: 'UTC-5' },
  { value: 'America/Lima', label: 'Lima', offset: 'UTC-5' },
  { value: 'America/Quito', label: 'Quito', offset: 'UTC-5' },
  { value: 'America/Caracas', label: 'Caracas', offset: 'UTC-4' },
  { value: 'America/La_Paz', label: 'La Paz', offset: 'UTC-4' },
  { value: 'America/Santiago', label: 'Santiago de Chile', offset: 'UTC-4' },
  { value: 'America/Asuncion', label: 'Asunción', offset: 'UTC-4' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires', offset: 'UTC-3' },
  { value: 'America/Montevideo', label: 'Montevideo', offset: 'UTC-3' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', offset: 'UTC-3' },
  { value: 'America/Havana', label: 'La Habana', offset: 'UTC-5' },
  { value: 'America/Santo_Domingo', label: 'Santo Domingo', offset: 'UTC-4' },
  { value: 'America/Puerto_Rico', label: 'San Juan (PR)', offset: 'UTC-4' },
  { value: 'America/New_York', label: 'New York', offset: 'UTC-5' },
  { value: 'America/Chicago', label: 'Chicago', offset: 'UTC-6' },
  { value: 'America/Denver', label: 'Denver', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Los Angeles', offset: 'UTC-8' },
  { value: 'America/Phoenix', label: 'Phoenix', offset: 'UTC-7' },
  { value: 'Europe/Madrid', label: 'Madrid', offset: 'UTC+1' },
  { value: 'Europe/Lisbon', label: 'Lisboa', offset: 'UTC+0' },
  { value: 'Europe/London', label: 'Londres', offset: 'UTC+0' },
  { value: 'UTC', label: 'UTC (sin DST)', offset: 'UTC+0' },
];
const TZ_OTHER = '__other__';

function detectLocalTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

type Step = 1 | 2 | 3 | 4;
type Provider = 'cloud-api' | 'evolution-go';
type Tone = 'friendly' | 'formal' | 'casual';
type Pronouns = 'tu' | 'usted';
type EmojiLevel = 'none' | 'minimal' | 'moderate' | 'heavy';

interface ProviderConfig {
  /** Cloud API */
  phoneNumberId?: string;
  /** Evolution Go */
  baseUrl?: string;
  instanceName?: string;
  /** Story 17-4 — menús: texto numerado o encuesta. */
  interactive?: MenuMode;
}

interface TimingConfig {
  /** Pausa default antes de cada mensaje saliente del bot. Cap 60_000. */
  defaultMessageDelayMs: number;
  /** Ventana para condensar mensajes consecutivos del usuario. 0 = off. Cap 30_000. */
  userBurstWindowMs: number;
}

interface State {
  step: Step;
  template: TemplateMetadataDto | null;
  answers: Record<string, string>;
  voice: { tone: Tone; pronouns: Pronouns; emojiLevel: EmojiLevel };
  provider: Provider;
  /** Story 13.x cleanup — datos operacionales NO secretos del provider
   *  (instanceName, baseUrl, phoneNumberId). Los secrets viven en
   *  organization_credentials, NO acá. */
  providerConfig: ProviderConfig;
  /** Realismo de tiempo: pausa entre mensajes del bot + condensación de
   *  ráfagas del usuario. Ambos defaults pensados para producción. */
  timing: TimingConfig;
  errors: Record<string, string>;
}

type Action =
  | { type: 'SET_STEP'; step: Step }
  | { type: 'SET_TEMPLATE'; template: TemplateMetadataDto }
  | { type: 'SET_ANSWER'; key: string; value: string }
  | { type: 'SET_VOICE'; voice: Partial<State['voice']> }
  | { type: 'SET_PROVIDER'; provider: Provider }
  | { type: 'SET_PROVIDER_CONFIG'; config: Partial<ProviderConfig> }
  | { type: 'SET_TIMING'; timing: Partial<TimingConfig> }
  | { type: 'SET_ERRORS'; errors: Record<string, string> }
  | { type: 'RESET' };

const initialState: State = {
  step: 1,
  template: null,
  answers: {},
  voice: { tone: 'friendly', pronouns: 'tu', emojiLevel: 'minimal' },
  provider: 'evolution-go',
  providerConfig: {},
  timing: { defaultMessageDelayMs: 1000, userBurstWindowMs: 4000 },
  errors: {},
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step, errors: {} };
    case 'SET_TEMPLATE':
      // Story 17-5 — los opcionales arrancan con el default del template
      // (el horario del restaurante); el operador los ve y los cambia.
      return {
        ...state,
        template: action.template,
        answers: { ...action.template.defaults },
        errors: {},
      };
    case 'SET_ANSWER':
      return {
        ...state,
        answers: { ...state.answers, [action.key]: action.value },
        errors: { ...state.errors, [action.key]: '' },
      };
    case 'SET_VOICE':
      return { ...state, voice: { ...state.voice, ...action.voice } };
    case 'SET_PROVIDER':
      return { ...state, provider: action.provider, providerConfig: {}, errors: {} };
    case 'SET_PROVIDER_CONFIG':
      return {
        ...state,
        providerConfig: { ...state.providerConfig, ...action.config },
        errors: {},
      };
    case 'SET_TIMING':
      return { ...state, timing: { ...state.timing, ...action.timing } };
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// Validación equivalente a la de `validateAnswerValue` en core, ejecutada
// client-side para feedback inmediato. Si falla aquí, NO disparamos el POST.
function validateAnswerValueClient(value: string): string | undefined {
  if (value.includes('"')) return 'No puede contener comillas dobles';
  if (value.includes('\\')) return 'No puede contener barras invertidas';
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if ((code >= 0 && code <= 0x1f) || code === 0x7f) {
      return 'No puede contener caracteres de control';
    }
  }
  return undefined;
}

function slugifyClient(input: string): string {
  return input
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function BotCreationWizard({ open, onClose }: Props): React.ReactElement | null {
  const [state, dispatch] = useReducer(reducer, initialState);
  // M-8 audit: errores de pre-validacion browser-side (StepReview).
  // Cuando hay errores, "Crear bot" queda deshabilitado hasta corregir.
  const [previewValidationErrors, setPreviewValidationErrors] = useState<BrowserValidationError[]>(
    [],
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Story 16.2 — si el usuario puede crear en varias organizaciones, elige en
  // cuál (el backend responde 400 si no). Con una sola, va implícita.
  const { data: me } = useMe();
  const editableOrgs = (me?.organizations ?? []).filter(
    (o) => o.role === 'owner' || o.role === 'editor',
  );
  const [organizationId, setOrganizationId] = useState<number | undefined>(undefined);
  const needsOrganization = editableOrgs.length > 1 && organizationId === undefined;

  const templatesQuery = useQuery({
    queryKey: ['bot-templates'],
    queryFn: () => botsApi.listTemplates(),
    enabled: open,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateBotFromTemplateBody) => botsApi.createFromTemplate(body),
    onSuccess: (response) => {
      const bot: BotRowSummary = response.bot;
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      toast.success(`Bot ${bot.name} creado. Configura las credenciales para activarlo.`);
      dispatch({ type: 'RESET' });
      onClose();
      // biome-ignore lint/suspicious/noExplicitAny: TanStack Router param typing relajado.
      void navigate({ to: '/bots/$botId' as any, params: { botId: String(bot.id) } as any });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      dispatch({ type: 'SET_ERRORS', errors: { _global: msg } });
    },
  });

  function handleClose(): void {
    if (createMutation.isPending) return;
    dispatch({ type: 'RESET' });
    onClose();
  }

  function validateIdentityStep(): boolean {
    const errs: Record<string, string> = {};
    const required = state.template?.requiredAnswers ?? [];
    for (const key of required) {
      const value = state.answers[key] ?? '';
      if (!value || value.trim().length === 0) {
        errs[key] = 'Campo requerido';
        continue;
      }
      const reason = validateAnswerValueClient(value);
      if (reason) errs[key] = reason;
    }
    if (state.answers.BOT_NAME && !/^[a-z0-9][a-z0-9-]{0,49}$/.test(state.answers.BOT_NAME)) {
      errs.BOT_NAME = 'Solo letras minúsculas, números y guiones (máx 50)';
    }
    // Story 17-5 — horas del template (OPENING_HOUR, CLOSING_HOUR): con otro
    // formato el bot no valida al crearlo.
    for (const [key, value] of Object.entries(state.answers)) {
      if (
        key.endsWith('_HOUR') &&
        value.trim() &&
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(value.trim())
      ) {
        errs[key] = 'Formato HH:MM (ej. 12:00)';
      }
    }
    // Story 13.x cleanup — validación E.164 para PHONE_NUMBER (paridad con CLI).
    if (state.answers.PHONE_NUMBER && !/^\+\d{10,15}$/.test(state.answers.PHONE_NUMBER.trim())) {
      errs.PHONE_NUMBER = 'Formato E.164 requerido: + seguido de 10–15 dígitos';
    }
    if (Object.keys(errs).filter((k) => errs[k]).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors: errs });
      return false;
    }
    return true;
  }

  function validateProviderStep(): boolean {
    const errs: Record<string, string> = {};
    if (state.provider === 'evolution-go') {
      const baseUrl = state.providerConfig.baseUrl?.trim() ?? '';
      const instanceName = state.providerConfig.instanceName?.trim() ?? '';
      if (!baseUrl) errs.baseUrl = 'Base URL requerida';
      else if (!/^https?:\/\//i.test(baseUrl)) errs.baseUrl = 'Debe empezar con http:// o https://';
      if (!instanceName) errs.instanceName = 'Nombre de instancia requerido';
      else if (!/^[a-zA-Z0-9_-]+$/.test(instanceName))
        errs.instanceName = 'Solo letras, números, guiones y guiones bajos';
    } else if (state.provider === 'cloud-api') {
      const phoneNumberId = state.providerConfig.phoneNumberId?.trim() ?? '';
      if (!phoneNumberId) errs.phoneNumberId = 'Phone Number ID requerido';
    }
    if (Object.keys(errs).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors: errs });
      return false;
    }
    return true;
  }

  function handleSubmit(): void {
    if (!state.template) return;
    // Si el template tiene TIMEZONE como placeholder y el user nunca tocó el
    // dropdown (queda en su default visual `detectLocalTz()`), `answers`
    // no lo trae — y el endpoint dejaría `{{TIMEZONE}}` literal en el
    // bot.yaml. Inyectamos el detect como fallback explícito acá para
    // que el answer viaje al backend.
    const placeholders = state.template.placeholders ?? [];
    const finalAnswers = { ...state.answers };
    if (placeholders.includes('TIMEZONE') && !finalAnswers.TIMEZONE) {
      finalAnswers.TIMEZONE = detectLocalTz();
    }
    createMutation.mutate({
      templateName: state.template.name,
      answers: finalAnswers,
      voice: state.voice,
      provider: state.provider,
      providerConfig: state.providerConfig,
      timing: state.timing,
      ...(organizationId !== undefined ? { organizationId } : {}),
    });
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Crear nuevo bot — paso ${state.step}/4`}
      description="Configura tu bot paso a paso. Las credenciales del proveedor se setean después de crearlo."
      className="max-w-3xl"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="ghost" onClick={handleClose} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <div className="flex gap-2">
            {state.step > 1 && (
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'SET_STEP', step: (state.step - 1) as Step })}
                disabled={createMutation.isPending}
              >
                ← Atrás
              </Button>
            )}
            {state.step < 4 && (
              <Button
                onClick={() => {
                  if (state.step === 1 && !state.template) return;
                  if (state.step === 2 && !validateIdentityStep()) return;
                  if (state.step === 3 && !validateProviderStep()) return;
                  dispatch({ type: 'SET_STEP', step: (state.step + 1) as Step });
                }}
                disabled={
                  (state.step === 1 && !state.template) || (state.step === 2 && !state.template)
                }
              >
                Siguiente →
              </Button>
            )}
            {state.step === 4 && (
              <Button
                onClick={handleSubmit}
                disabled={
                  createMutation.isPending ||
                  previewValidationErrors.length > 0 ||
                  needsOrganization
                }
                title={
                  previewValidationErrors.length > 0
                    ? 'Corrige los errores de validación primero'
                    : needsOrganization
                      ? 'Elige la organización del bot'
                      : undefined
                }
              >
                {createMutation.isPending ? 'Creando…' : 'Crear bot'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {state.errors._global && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {state.errors._global}
          </div>
        )}

        {state.step === 1 && (
          <StepTemplate
            templates={templatesQuery.data ?? []}
            isLoading={templatesQuery.isLoading}
            isError={templatesQuery.isError}
            selected={state.template}
            onSelect={(t) => dispatch({ type: 'SET_TEMPLATE', template: t })}
            onRetry={() => templatesQuery.refetch()}
          />
        )}

        {state.step === 2 && state.template && (
          <StepIdentity
            template={state.template}
            answers={state.answers}
            voice={state.voice}
            timing={state.timing}
            errors={state.errors}
            onAnswer={(key, value) => dispatch({ type: 'SET_ANSWER', key, value })}
            onVoice={(voice) => dispatch({ type: 'SET_VOICE', voice })}
            onTiming={(timing) => dispatch({ type: 'SET_TIMING', timing })}
          />
        )}

        {state.step === 3 && (
          <StepProvider
            provider={state.provider}
            providerConfig={state.providerConfig}
            errors={state.errors}
            onChangeProvider={(p) => dispatch({ type: 'SET_PROVIDER', provider: p })}
            onChangeConfig={(config) => dispatch({ type: 'SET_PROVIDER_CONFIG', config })}
          />
        )}

        {state.step === 4 && editableOrgs.length > 1 && (
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Organización</span>
            <select
              className="w-full rounded-md border bg-background px-3 py-2"
              value={organizationId ?? ''}
              onChange={(e) =>
                setOrganizationId(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">Elige dónde crear el bot…</option>
              {editableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {state.step === 4 && state.template && (
          <StepReview
            template={state.template}
            answers={state.answers}
            voice={state.voice}
            provider={state.provider}
            providerConfig={state.providerConfig}
            onValidationChange={setPreviewValidationErrors}
          />
        )}
      </div>
    </Modal>
  );
}

// ---------- Step components ----------

interface StepTemplateProps {
  templates: TemplateMetadataDto[];
  isLoading: boolean;
  isError: boolean;
  selected: TemplateMetadataDto | null;
  onSelect: (t: TemplateMetadataDto) => void;
  onRetry: () => void;
}

function StepTemplate({
  templates,
  isLoading,
  isError,
  selected,
  onSelect,
  onRetry,
}: StepTemplateProps): React.ReactElement {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando templates…</p>;
  }
  if (isError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">No se pudieron cargar los templates.</p>
        <Button variant="outline" onClick={onRetry}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (templates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hay templates disponibles en este servidor.
      </p>
    );
  }
  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Elige un template</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {templates.map((t) => {
          const isSel = selected?.name === t.name;
          return (
            <button
              key={t.name}
              type="button"
              onClick={() => onSelect(t)}
              className={cn(
                'rounded-md border p-4 text-left transition-colors',
                isSel ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium">{t.name}</div>
                <div className="flex shrink-0 items-center gap-1">
                  {t.kind === 'user' && (
                    <span className="rounded bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                      Mío
                    </span>
                  )}
                  {t.category && (
                    <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                      {t.category}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{t.description}</div>
              <div className="mt-2 text-xs text-muted-foreground">
                {t.requiredAnswers.length} campo{t.requiredAnswers.length === 1 ? '' : 's'}{' '}
                requerido
                {t.requiredAnswers.length === 1 ? '' : 's'}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface StepIdentityProps {
  template: TemplateMetadataDto;
  answers: Record<string, string>;
  voice: State['voice'];
  timing: TimingConfig;
  errors: Record<string, string>;
  onAnswer: (key: string, value: string) => void;
  onVoice: (voice: Partial<State['voice']>) => void;
  onTiming: (timing: Partial<TimingConfig>) => void;
}

/** Story 17-5 — nombre de cada campo conocido; los demás muestran su key. */
const FIELD_LABELS: Record<string, string> = {
  BOT_NAME: 'Identificador del bot',
  BUSINESS_NAME: 'Nombre del negocio',
  BUSINESS_TYPE: 'Tipo de negocio',
  LANGUAGE: 'Idioma',
  PHONE_NUMBER: 'Teléfono de contacto',
  OPENING_HOUR: 'Hora de apertura',
  CLOSING_HOUR: 'Hora de cierre',
};

function StepIdentity({
  template,
  answers,
  voice,
  timing,
  errors,
  onAnswer,
  onVoice,
  onTiming,
}: StepIdentityProps): React.ReactElement {
  const placeholders = template.placeholders ?? [];
  const requiredSet = new Set(template.requiredAnswers ?? []);

  const optionsFor = (key: string): string[] | null => {
    if (template.enums?.[key]) return template.enums[key];
    if (key === 'BUSINESS_TYPE') {
      return template.businessTypes ?? ['restaurante', 'retail', 'servicios', 'salud', 'otro'];
    }
    if (key === 'LANGUAGE') return ['es', 'en'];
    return null;
  };

  // Story 13.x cleanup — el wizard ahora maneja TIMEZONE como caso
  // especial: select con presets + sentinel "Otra…" que muestra un
  // input libre. Igual que el CLI.
  function renderTimezoneField(key: string, required: boolean, error?: string) {
    const current = answers[key] ?? detectLocalTz();
    const isInPreset = TIMEZONE_PRESETS.some((tz) => tz.value === current);
    const selectValue = isInPreset ? current : TZ_OTHER;
    return (
      <div key={key}>
        <label htmlFor={`field-${key}`} className="mb-1 block text-sm font-medium">
          Timezone{required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <select
          id={`field-${key}`}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === TZ_OTHER) {
              onAnswer(key, isInPreset ? '' : current);
            } else {
              onAnswer(key, v);
            }
          }}
        >
          {TIMEZONE_PRESETS.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label} ({tz.offset})
            </option>
          ))}
          <option value={TZ_OTHER}>Otra (especificar)…</option>
        </select>
        {selectValue === TZ_OTHER && (
          <input
            type="text"
            className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
            placeholder="ej. Africa/Cairo, Asia/Tokyo"
            value={current === detectLocalTz() && !isInPreset ? '' : current}
            onChange={(e) => onAnswer(key, e.target.value)}
          />
        )}
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Identidad y voz del bot</h2>
      <div className="space-y-3">
        {placeholders.map((key) => {
          if (key === 'TIMEZONE') {
            return renderTimezoneField(key, requiredSet.has(key), errors[key]);
          }
          const opts = optionsFor(key);
          const value = answers[key] ?? '';
          const required = requiredSet.has(key);
          const error = errors[key];
          const placeholder = key === 'BOT_NAME' ? slugifyClient(answers.BUSINESS_NAME ?? '') : '';
          const inputType = key === 'PHONE_NUMBER' ? 'tel' : 'text';
          const inputPlaceholder =
            key === 'PHONE_NUMBER' ? '+5215587021866' : placeholder || undefined;
          return (
            <div key={key}>
              <label htmlFor={`field-${key}`} className="mb-1 block text-sm font-medium">
                {FIELD_LABELS[key] ?? key}
                {required && <span className="ml-1 text-destructive">*</span>}
              </label>
              {opts ? (
                <select
                  id={`field-${key}`}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={value}
                  onChange={(e) => onAnswer(key, e.target.value)}
                >
                  <option value="">— elegir —</option>
                  {opts.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`field-${key}`}
                  type={inputType}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={value}
                  placeholder={inputPlaceholder}
                  onChange={(e) => onAnswer(key, e.target.value)}
                />
              )}
              {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
            </div>
          );
        })}

        <div className="border-t pt-3">
          <h3 className="mb-2 text-sm font-medium">Voz del bot</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor="voice-tone" className="mb-1 block text-xs">
                Tono
              </label>
              <select
                id="voice-tone"
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                value={voice.tone}
                onChange={(e) => onVoice({ tone: e.target.value as Tone })}
              >
                <option value="friendly">Amigable</option>
                <option value="formal">Formal</option>
                <option value="casual">Casual</option>
              </select>
            </div>
            <div>
              <label htmlFor="voice-pronouns" className="mb-1 block text-xs">
                Pronombre
              </label>
              <select
                id="voice-pronouns"
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                value={voice.pronouns}
                onChange={(e) => onVoice({ pronouns: e.target.value as Pronouns })}
              >
                <option value="tu">Tú</option>
                <option value="usted">Usted</option>
              </select>
            </div>
            <div>
              <label htmlFor="voice-emoji" className="mb-1 block text-xs">
                Emojis
              </label>
              <select
                id="voice-emoji"
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                value={voice.emojiLevel}
                onChange={(e) => onVoice({ emojiLevel: e.target.value as EmojiLevel })}
              >
                <option value="none">Sin emojis</option>
                <option value="minimal">Pocos</option>
                <option value="moderate">Moderados</option>
                <option value="heavy">Muchos</option>
              </select>
            </div>
          </div>
        </div>

        {/* ─── Realismo de tiempo ──────────────────────────────────── */}
        <div>
          <h3 className="mb-2 mt-4 text-sm font-semibold">Realismo de tiempo</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Cómo se siente el ritmo de la conversación. Los valores por defecto funcionan bien para
            la mayoría de los bots.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="timing-msg-delay" className="mb-1 block text-xs">
                Pausa antes de cada mensaje del bot
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="timing-msg-delay"
                  type="number"
                  min={0}
                  max={60_000}
                  step={100}
                  value={timing.defaultMessageDelayMs}
                  onChange={(e) =>
                    onTiming({
                      defaultMessageDelayMs: Math.max(
                        0,
                        Math.min(60_000, Number(e.target.value) || 0),
                      ),
                    })
                  }
                  className="w-32 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                />
                <span className="text-xs text-muted-foreground">ms</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Simula que el bot "está escribiendo". 0 = instantáneo. Sugerido: 800–1500 ms.
              </p>
            </div>
            <div>
              <label htmlFor="timing-burst" className="mb-1 block text-xs">
                Condensar mensajes en ráfaga del usuario
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="timing-burst"
                  type="number"
                  min={0}
                  max={30_000}
                  step={500}
                  value={timing.userBurstWindowMs}
                  onChange={(e) =>
                    onTiming({
                      userBurstWindowMs: Math.max(0, Math.min(30_000, Number(e.target.value) || 0)),
                    })
                  }
                  className="w-32 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                />
                <span className="text-xs text-muted-foreground">ms</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Si el user manda varios mensajes seguidos en este tiempo, el bot los procesa como
                uno solo. 0 = procesa cada mensaje al instante. Sugerido: 3000–5000 ms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepProvider({
  provider,
  providerConfig,
  errors,
  onChangeProvider,
  onChangeConfig,
}: {
  provider: Provider;
  providerConfig: ProviderConfig;
  errors: Record<string, string>;
  onChangeProvider: (p: Provider) => void;
  onChangeConfig: (config: Partial<ProviderConfig>) => void;
}): React.ReactElement {
  const options: Array<{ value: Provider; label: string; desc: string }> = [
    {
      value: 'cloud-api',
      label: 'WhatsApp Cloud API',
      desc: 'Canal oficial de Meta. Requiere app aprobada por Meta Business Manager.',
    },
    {
      value: 'evolution-go',
      label: 'Evolution Go',
      desc: 'Self-hosted en tu VPS. Sin aprobación de Meta. Mensajes interactivos se degradan automáticamente a texto numerado.',
    },
  ];
  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Proveedor de WhatsApp</h2>
      <div className="space-y-3" role="radiogroup" aria-label="Proveedor de WhatsApp">
        {options.map((opt) => {
          const isSel = provider === opt.value;
          return (
            // biome-ignore lint/a11y/useSemanticElements: tarjeta seleccionable con descripción; un <input type="radio"> nativo no admite este layout.
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isSel}
              onClick={() => onChangeProvider(opt.value)}
              className={cn(
                'w-full rounded-md border p-4 text-left',
                isSel ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
              )}
            >
              <div className="font-medium">{opt.label}</div>
              <div className="mt-1 text-sm text-muted-foreground">{opt.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Story 13.x cleanup — datos operacionales (NO secrets) van al
          bot.config y se piden ya en el wizard. Las credenciales secretas
          (apiKey / accessToken / webhook secret) se configuran luego en la
          página del bot, en CredentialsPanel. */}
      {provider === 'evolution-go' && (
        <div className="mt-4 space-y-3 rounded-md border border-border p-4">
          <h3 className="text-sm font-medium">Identificadores de la instancia</h3>
          <div>
            <label htmlFor="prov-baseurl" className="mb-1 block text-sm">
              Base URL del server Evolution Go
              <span className="ml-1 text-destructive">*</span>
            </label>
            <input
              id="prov-baseurl"
              type="url"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              value={providerConfig.baseUrl ?? ''}
              placeholder="https://go.miempresa.com"
              onChange={(e) => onChangeConfig({ baseUrl: e.target.value })}
            />
            {errors.baseUrl && <p className="mt-1 text-xs text-destructive">{errors.baseUrl}</p>}
          </div>
          <div>
            <label htmlFor="prov-instance" className="mb-1 block text-sm">
              Nombre de la instancia
              <span className="ml-1 text-destructive">*</span>
            </label>
            <input
              id="prov-instance"
              type="text"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              value={providerConfig.instanceName ?? ''}
              placeholder="ej. mi-bot-prod"
              onChange={(e) => onChangeConfig({ instanceName: e.target.value })}
            />
            {errors.instanceName && (
              <p className="mt-1 text-xs text-destructive">{errors.instanceName}</p>
            )}
          </div>
          <MenuModeSelect
            id="prov-interactive"
            value={providerConfig.interactive ?? 'text'}
            onChange={(interactive) => onChangeConfig({ interactive })}
          />
          <p className="text-xs text-muted-foreground">
            La <strong>API key</strong> de la instancia es de la organización: su owner la carga una
            vez en la página de cualquiera de sus bots (sección Credenciales). Se cifra antes de
            guardar en BD.
          </p>
        </div>
      )}

      {provider === 'cloud-api' && (
        <div className="mt-4 space-y-3 rounded-md border border-border p-4">
          <h3 className="text-sm font-medium">Identificador de Meta</h3>
          <div>
            <label htmlFor="prov-phoneid" className="mb-1 block text-sm">
              Phone Number ID<span className="ml-1 text-destructive">*</span>
            </label>
            <input
              id="prov-phoneid"
              type="text"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              value={providerConfig.phoneNumberId ?? ''}
              placeholder="123456789012345"
              onChange={(e) => onChangeConfig({ phoneNumberId: e.target.value })}
            />
            {errors.phoneNumberId && (
              <p className="mt-1 text-xs text-destructive">{errors.phoneNumberId}</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            El <strong>access token</strong> se configura después, en la página del bot (sección
            Credenciales). Se cifra antes de guardar en BD.
          </p>
        </div>
      )}
    </div>
  );
}

// Replicate placeholder substitution para preview + validation (no usamos
// `substitutePlaceholders` del core para no arrastrar deps node-only al
// bundle del browser; el endpoint hace lo mismo server-side).
function applyPlaceholders(content: string, answers: Record<string, string>): string {
  let out = content;
  for (const [k, v] of Object.entries(answers)) {
    out = out.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
  }
  return out;
}

export interface BrowserValidationError {
  code: string;
  message: string;
  line?: number;
  filePath?: string;
  suggestion?: string;
}

/**
 * Pre-validacion browser-side (gap M-8 audit) — resuelve el bot en memoria
 * (parsea bot.yaml + flows + voice + hours del template files map) y delega
 * a `loadAndValidateBotFromConfig` del subexport `@revelation-bots/core/dist/
 * validator-browser`. Defense-in-depth: el backend tambien valida tras el
 * POST, pero si los errores se detectan antes el operador no pierde
 * contexto del wizard.
 */
/** Mapea un RevelError del core a la forma que renderiza StepReview. */
function toBrowserError(e: unknown): BrowserValidationError {
  return {
    code: (e as { code?: string }).code ?? 'DSL_VALIDATION_ERROR',
    message: (e as { message?: string }).message ?? String(e),
    line: (e as { location?: { line?: number } }).location?.line,
    filePath: (e as { location?: { file?: string } }).location?.file,
    suggestion: (e as { suggestion?: string }).suggestion,
  };
}

export async function validateBotPreview(
  files: Record<string, string>,
  answers: Record<string, string>,
): Promise<{ ok: true } | { ok: false; errors: BrowserValidationError[] }> {
  let validatorMod: typeof import('@revelation-bots/core/validator-browser');
  try {
    // El subexport `validator-browser` evita el barrel principal de core
    // que arrastra deps node-only (fs/path/events) — rompe el bundle Vite.
    validatorMod = await import('@revelation-bots/core/validator-browser');
  } catch (err) {
    // Si el subexport no esta disponible (build issue), no bloqueamos al
    // operador — el backend revalidara despues. Solo loggeamos.
    // biome-ignore lint/suspicious/noConsole: dev-only fallback hint
    console.warn('Browser validator no disponible, skip pre-validate', err);
    return { ok: true };
  }
  const { parseBotDefinition, parseFlowFile, loadAndValidateBotFromConfig } = validatorMod;

  const botYamlRaw = files['bot.yaml'];
  if (typeof botYamlRaw !== 'string') {
    return {
      ok: false,
      errors: [{ code: 'TEMPLATE_INVALID', message: 'Falta bot.yaml en el template' }],
    };
  }

  // 1. Parsear + NORMALIZAR bot.yaml con el parser real del core. Esto
  //    convierte snake_case → camelCase (entry_flow→entryFlow, emoji_level→
  //    emojiLevel, send_message→sendMessage…) igual que hace el backend; sin
  //    esto `loadAndValidateBotFromConfig` (que espera la forma normalizada)
  //    reportaría errores espurios sobre templates válidos. `resolveSecrets:
  //    false` evita que el parser intente interpolar `${ENV_VARS}` (no
  //    disponibles en el browser).
  const botParse = parseBotDefinition(applyPlaceholders(botYamlRaw, answers), 'bot.yaml', {
    resolveSecrets: false,
  });
  if (!botParse.success) {
    return { ok: false, errors: botParse.errors.map(toBrowserError) };
  }
  const bot = botParse.data;

  // 2. Resolver flows externos (string refs como `welcome: "flows/welcome.yaml"`)
  //    leyendo del map de files y parseándolos con el parser real.
  const errors: BrowserValidationError[] = [];
  if (bot.externalFlows) {
    for (const [name, ref] of Object.entries(bot.externalFlows)) {
      const content = files[ref.path];
      if (typeof content !== 'string') {
        errors.push({
          code: 'DSL_FILE_NOT_FOUND',
          message: `Flow file no encontrado: ${ref.path}`,
          filePath: 'bot.yaml',
          line: ref._source?.line,
        });
        continue;
      }
      const flowParse = parseFlowFile(applyPlaceholders(content, answers), ref.path, name);
      if (!flowParse.success) {
        errors.push(...flowParse.errors.map(toBrowserError));
        continue;
      }
      bot.flows[name] = flowParse.data;
    }
    // Ya inlineados; limpiamos para que el validador no los exija como externos.
    bot.externalFlows = undefined;
  }
  if (errors.length > 0) return { ok: false, errors };

  // 3. Validar el bot ya normalizado vía la función del AC #8.
  const result = loadAndValidateBotFromConfig(bot as unknown as Record<string, unknown>);
  if (!result.success) {
    return { ok: false, errors: result.errors.map(toBrowserError) };
  }
  return { ok: true };
}

function StepReview({
  template,
  answers,
  voice,
  provider,
  providerConfig,
  onValidationChange,
}: {
  template: TemplateMetadataDto;
  answers: Record<string, string>;
  voice: State['voice'];
  provider: Provider;
  providerConfig: ProviderConfig;
  onValidationChange: (errors: BrowserValidationError[]) => void;
}): React.ReactElement {
  const [previewYaml, setPreviewYaml] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<BrowserValidationError[]>([]);

  // Cargar template files y construir YAML preview client-side.
  // Una sola vez por mount del step (StaleTime 60s).
  const previewQuery = useQuery({
    queryKey: ['bot-template-preview', template.name],
    queryFn: () => botsApi.getTemplate(template.name),
    staleTime: 60_000,
  });

  if (!previewQuery.isLoading && previewQuery.data && !previewYaml) {
    const botYaml = previewQuery.data.files['bot.yaml'] ?? '';
    setPreviewYaml(applyPlaceholders(botYaml, answers));
    // Pre-validar (gap M-8 audit). Async: no bloquea render del preview,
    // pero si retorna errores deshabilita el boton "Crear bot" via
    // onValidationChange.
    void validateBotPreview(previewQuery.data.files, answers).then((res) => {
      const errs = res.ok ? [] : res.errors;
      setValidationErrors(errs);
      onValidationChange(errs);
    });
  }

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold">Revisar y crear</h2>
      <table className="mb-3 w-full text-sm">
        <tbody>
          <tr>
            <td className="py-1 text-muted-foreground">Template</td>
            <td className="py-1">{template.name}</td>
          </tr>
          <tr>
            <td className="py-1 text-muted-foreground">Nombre</td>
            <td className="py-1">{answers.BOT_NAME ?? '(sin nombre)'}</td>
          </tr>
          <tr>
            <td className="py-1 text-muted-foreground">Negocio</td>
            <td className="py-1">{answers.BUSINESS_NAME ?? ''}</td>
          </tr>
          <tr>
            <td className="py-1 text-muted-foreground">Idioma / TZ</td>
            <td className="py-1">
              {answers.LANGUAGE ?? '?'} / {answers.TIMEZONE ?? detectLocalTz()}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-muted-foreground">Voz</td>
            <td className="py-1">
              {voice.tone}, {voice.pronouns}, emojis {voice.emojiLevel}
            </td>
          </tr>
          <tr>
            <td className="py-1 text-muted-foreground">Proveedor</td>
            <td className="py-1">{provider}</td>
          </tr>
          {provider === 'evolution-go' && (
            <>
              <tr>
                <td className="py-1 text-muted-foreground">Base URL</td>
                <td className="py-1 font-mono text-xs">{providerConfig.baseUrl ?? '—'}</td>
              </tr>
              <tr>
                <td className="py-1 text-muted-foreground">Instance</td>
                <td className="py-1 font-mono text-xs">{providerConfig.instanceName ?? '—'}</td>
              </tr>
              <tr>
                <td className="py-1 text-muted-foreground">Menús</td>
                <td className="py-1">{MENU_MODE_LABELS[providerConfig.interactive ?? 'text']}</td>
              </tr>
            </>
          )}
          {provider === 'cloud-api' && (
            <tr>
              <td className="py-1 text-muted-foreground">Phone Number ID</td>
              <td className="py-1 font-mono text-xs">{providerConfig.phoneNumberId ?? '—'}</td>
            </tr>
          )}
        </tbody>
      </table>
      {validationErrors.length > 0 && (
        <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <p className="mb-2 text-sm font-semibold text-destructive">
            {validationErrors.length} error{validationErrors.length === 1 ? '' : 'es'} de validación
            bloquean la creación
          </p>
          <ul className="space-y-1 text-xs">
            {validationErrors.slice(0, 6).map((e, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: stable order, list never reorders
                key={`${e.code}-${i}`}
                className="rounded border-l-2 border-destructive/60 bg-destructive/5 px-2 py-1"
              >
                <p className="font-mono text-[10px]">{e.code}</p>
                <p className="text-foreground">{e.message}</p>
                {(e.filePath || e.line) && (
                  <p className="text-[10px] text-muted-foreground">
                    {e.filePath ?? 'bot.yaml'}
                    {e.line != null ? `:${e.line}` : ''}
                  </p>
                )}
                {e.suggestion && (
                  <p className="text-[10px] text-muted-foreground">💡 {e.suggestion}</p>
                )}
              </li>
            ))}
            {validationErrors.length > 6 && (
              <li className="text-[10px] text-muted-foreground">
                …y {validationErrors.length - 6} más.
              </li>
            )}
          </ul>
        </div>
      )}
      <div>
        <p className="mb-1 text-sm font-medium">bot.yaml (preview):</p>
        <pre className="max-h-72 overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs">
          {previewYaml || 'Generando preview…'}
        </pre>
      </div>
    </div>
  );
}
